import { PreviewController, type PreviewSnapshot } from './preview';
import { LIVE_IDLE_MS, LiveView, liveViewSupported, type LiveViewSnapshot } from './liveView';

/*
 * One preview per stream for the whole app (owner, 18.10: switching Studio ⇄ Status must not
 * lose the picture). The controller draws into an off-screen master canvas that outlives the
 * pages; every StreamPreview on screen copies the master into its own canvas. A page change
 * unmounts one view and mounts another: the master keeps its frame and its request timing
 * (no fresh request that the server's 2 s limit would answer with 429), and the new view
 * shows the last frame at once. With no view left the controller stops after LINGER_MS.
 *
 * Low traffic (owner, 18.10, item 16) is one switch for the app, remembered in this browser:
 * on — the keyframe preview above; off — the real-time live view from the node (liveView.ts),
 * while the keyframe polling rests. Default: on for a phone, off otherwise. The live view
 * stops while the tab is hidden and after LIVE_IDLE_MS without a pointer or key.
 */

/* ── the Low traffic switch ───────────────────────────────────────────────── */

const LOW_KEY = 'rs.preview.lowTraffic';
const lowListeners = new Set<(v: boolean) => void>();

function defaultLow(): boolean {
  try {
    const v = localStorage.getItem(LOW_KEY);
    if (v === '1' || v === '0') return v === '1';
  } catch {
    // storage blocked: the default
  }
  return typeof matchMedia === 'function' && matchMedia('(max-width: 767px)').matches;
}

let low = defaultLow();

export function lowTraffic(): boolean {
  return low;
}

export function setLowTraffic(v: boolean) {
  if (v === low) return;
  low = v;
  try {
    localStorage.setItem(LOW_KEY, v ? '1' : '0');
  } catch {
    // not remembered: still applies now
  }
  for (const l of lowListeners) l(v);
}

export function onLowTraffic(l: (v: boolean) => void): () => void {
  lowListeners.add(l);
  return () => lowListeners.delete(l);
}

/* ── activity: the live view stops after LIVE_IDLE_MS without it ───────────── */

let lastActivity = Date.now();
if (typeof window !== 'undefined') {
  const mark = () => (lastActivity = Date.now());
  for (const e of ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart']) window.addEventListener(e, mark, { passive: true });
}

/** the master's backing size: 16:9, sharp enough for the Studio stage at 2× */
export const MASTER_W = 1280;
export const MASTER_H = 720;
/** how long a preview nobody shows keeps running: covers a page change and React's double mount */
export const LINGER_MS = 15_000;

type Listener = (s: PreviewSnapshot) => void;
type LiveListener = (s: LiveViewSnapshot) => void;

export class SharedPreview {
  readonly canvas: HTMLCanvasElement;
  private ctl: PreviewController;
  private listeners = new Set<Listener>();
  private liveListeners = new Set<LiveListener>();
  private refs = 0;
  private linger: ReturnType<typeof setTimeout> | undefined;
  private available: boolean;
  private idleTimer: ReturnType<typeof setInterval> | undefined;
  private offLow: () => void;
  readonly live: LiveView;
  snap: PreviewSnapshot;

  constructor(readonly stream: string, available: boolean, private onGone: () => void) {
    this.available = available;
    this.live = new LiveView(stream, (s) => {
      for (const l of this.liveListeners) l(s);
      this.apply();
    });
    this.canvas = document.createElement('canvas');
    this.canvas.width = MASTER_W;
    this.canvas.height = MASTER_H;
    this.ctl = new PreviewController(this.canvas, stream, (s) => {
      this.snap = s;
      for (const l of this.listeners) l(s);
    });
    this.snap = this.ctl.snapshot;
    this.ctl.start(available);
    this.offLow = onLowTraffic(() => this.reconcile());
    document.addEventListener('visibilitychange', this.onVisibility);
    this.idleTimer = setInterval(() => {
      if (this.live.snap.state === 'playing' && Date.now() - lastActivity > LIVE_IDLE_MS) this.live.stop('idle_timeout');
    }, 15_000);
    this.reconcile();
  }

  private onVisibility = () => this.reconcile();

  /** Low traffic off, the stream live, the tab visible: the live view runs; else it stops. */
  private reconcile() {
    const want = !low && this.available && !document.hidden && liveViewSupported();
    const st = this.live.snap.state;
    if (want && st === 'idle') void this.live.start(false);
    // hidden tab: stop playing but keep "busy" / "taken" / "idle" messages; Low traffic on or the
    // stream gone: back to plain keyframes (a later switch-off tries the live view again)
    else if (!want && st !== 'idle' && (low || !this.available || st === 'playing' || st === 'connecting')) this.live.stop('idle');
    this.apply();
  }

  /** The keyframe polling rests while the live view plays. */
  private apply() {
    this.ctl.setAvailable(this.available && this.live.snap.state !== 'playing');
  }

  /** "Start viewing": take the live view over from the other window (or start again after idle). */
  startViewing() {
    lastActivity = Date.now();
    void this.live.start(this.live.snap.state === 'busy' || this.live.snap.state === 'taken');
  }

  subscribeLive(l: LiveListener): () => void {
    this.liveListeners.add(l);
    return () => this.liveListeners.delete(l);
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  setAvailable(available: boolean) {
    if (available === this.available) return;
    this.available = available;
    this.reconcile();
  }

  hold() {
    this.refs++;
    if (this.linger !== undefined) clearTimeout(this.linger);
    this.linger = undefined;
  }

  release() {
    this.refs = Math.max(0, this.refs - 1);
    if (this.refs > 0 || this.linger !== undefined) return;
    this.linger = setTimeout(() => {
      this.linger = undefined;
      if (this.refs > 0) return;
      this.ctl.stop();
      this.live.stop();
      this.offLow();
      document.removeEventListener('visibilitychange', this.onVisibility);
      clearInterval(this.idleTimer);
      this.onGone();
    }, LINGER_MS);
  }
}

const hub = new Map<string, SharedPreview>();

/** The shared preview of a stream; hold() it while shown, release() when not. */
export function sharedPreview(stream: string, available: boolean): SharedPreview {
  let p = hub.get(stream);
  if (!p) {
    p = new SharedPreview(stream, available, () => {
      if (hub.get(stream) === p) hub.delete(stream);
    });
    hub.set(stream, p);
  }
  return p;
}

/** Copies the master into a view canvas, letterboxing is already in the master. */
export function blit(from: HTMLCanvasElement, to: HTMLCanvasElement) {
  const ctx = to.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, to.width, to.height);
  ctx.drawImage(from, 0, 0, to.width, to.height);
}
